import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "dropzone", "fileInput", "fileName", "submitButton",
    "shiftModal", "shiftTitle", "shiftTime", "shiftDuration", "shiftRequired", "shiftWeekdays", "shiftRoles",
    "modal", "title", "content", "time", "duration", "required", "weekdays", "roles",
    // Day detail modal targets
    "dayModal", "dayModalTitle", "dayModalContent",
    // Shift detail modal targets
    "shiftDetailModal", "shiftDetailTitle", "shiftDetailDate", "shiftDetailContent"
  ]
  static values = {
    id: String,
    autoOpen: { type: Boolean, default: false },
    targetId: String,
    triggerAction: String,
    triggerModalId: String,
    autoDismiss: { type: Number, default: 0 },
    invalidCsvMessage: { type: String, default: "Please select a valid CSV file" }
  }

  connect() {
    if (this.hasTriggerActionValue && this.hasTriggerModalIdValue) {
      requestAnimationFrame(() => {
        this.handleTriggerAction()
        this.element.remove()
      })
      return
    }

    // Auto-dismiss flash messages
    if (this.autoDismissValue > 0) {
      this.dismissTimeout = setTimeout(() => this.element.remove(), this.autoDismissValue)
      return
    }

    // Reset modal state on (re)connect — prevents Turbo page cache
    // from restoring a previously-opened modal
    if (this.isOpen()) {
      this.element.classList.add("hidden")
      document.body.style.overflow = ""
    }

    this.boundHandleEscape = this.handleEscape.bind(this)
    document.addEventListener("keydown", this.boundHandleEscape)

    // Close modal before Turbo caches the page
    this.boundBeforeCache = () => this.close()
    document.addEventListener("turbo:before-cache", this.boundBeforeCache)

    // Auto-open: for modals rendered via turbo-frame responses that should
    // immediately show. Uses a one-time flag to prevent reopening on Turbo
    // cache restore.
    if (this.autoOpenValue && !this.element.dataset.modalAutoOpened) {
      const isPreview = document.documentElement.hasAttribute("data-turbo-preview")
      if (!isPreview) {
        this.element.dataset.modalAutoOpened = "true"
        this.open()
      }
    }

    this.setupFrameLoadListener()
    this.setupCustomEventListener()
  }

  disconnect() {
    if (this.dismissTimeout) clearTimeout(this.dismissTimeout)
    document.removeEventListener("keydown", this.boundHandleEscape)

    if (this.boundBeforeCache) {
      document.removeEventListener("turbo:before-cache", this.boundBeforeCache)
    }

    if (this.boundFrameLoadHandler) {
      const frame = this.element.querySelector('turbo-frame[data-action*="turbo:frame-load->modal#open"]')
      if (frame) {
        frame.removeEventListener('turbo:frame-load', this.boundFrameLoadHandler)
      }
    }

    if (this.boundCustomOpenHandler && this.hasIdValue) {
      document.removeEventListener(`modal:open:${this.idValue}`, this.boundCustomOpenHandler)
    }

  }

  setupFrameLoadListener() {
    const frame = this.element.querySelector('turbo-frame[data-action*="turbo:frame-load->modal#open"]')
    if (frame) {
      this.boundFrameLoadHandler = () => {
        // Only open if the frame has actual content (not empty placeholder)
        const hasContent = frame.querySelector("form, [data-modal-content]") ||
                           (frame.textContent.trim().length > 0 && !frame.querySelector("[data-placeholder]"))
        if (hasContent) {
          requestAnimationFrame(() => this.open())
        }
      }
      frame.addEventListener('turbo:frame-load', this.boundFrameLoadHandler)
    }
  }

  setupCustomEventListener() {
    if (this.hasIdValue) {
      this.boundCustomOpenHandler = () => this.open()
      document.addEventListener(`modal:open:${this.idValue}`, this.boundCustomOpenHandler)
    }
  }


  open(event) {
    event?.preventDefault()
    this.element.classList.remove("hidden")
    this.element.classList.add("flex")
    document.body.style.overflow = "hidden"
  }

  openOnFrameLoad() {
    const modal = this.element.querySelector('[data-controller="modal"]')
    if (modal) {
      const modalController = this.application.getControllerForElementAndIdentifier(modal, "modal")
      if (modalController) {
        modalController.open()
      }
    }
  }

  close() {
    this.element.classList.add("hidden")
    this.element.classList.remove("flex")
    document.body.style.overflow = ""
  }

  // Alias for close — used by trigger buttons inside modal content
  closeFromTrigger() {
    this.close()
  }

  toggle() {
    this.isOpen() ? this.close() : this.open()
  }

  trigger(event) {
    event?.preventDefault()

    if (!this.hasTargetIdValue) return

    const modal = document.getElementById(this.targetIdValue)
    if (!modal) return

    const modalController = this.application.getControllerForElementAndIdentifier(modal, "modal")
    if (modalController) {
      modalController.open()
    }
  }

  isOpen() {
    return !this.element.classList.contains("hidden")
  }

  handleEscape(event) {
    if (event.key === "Escape") {
      // Check modals in priority order (most recently opened first)
      if (this.hasShiftDetailModalTarget && !this.shiftDetailModalTarget.classList.contains("hidden")) {
        this.closeShiftDetailModal()
      } else if (this.hasDayModalTarget && !this.dayModalTarget.classList.contains("hidden")) {
        this.closeDayModal()
      } else if (this.hasModalTarget && !this.modalTarget.classList.contains("hidden")) {
        this.closeModalTarget()
      } else if (this.isOpen()) {
        this.close()
      }
    }
  }

  closeOnBackdrop(event) {
    if (event.target === this.element) {
      this.close()
    }
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  handleTriggerAction() {
    if (!this.hasTriggerActionValue || !this.hasTriggerModalIdValue) return

    const modal = document.getElementById(this.triggerModalIdValue)
    if (!modal) return

    const modalController = this.application.getControllerForElementAndIdentifier(modal, "modal")
    if (!modalController) return

    if (this.triggerActionValue === "close") {
      modalController.close()
    } else if (this.triggerActionValue === "open") {
      modalController.open()
    }
  }

  openModal(event) {
    event?.preventDefault()

    const modalId = event?.currentTarget?.dataset?.modalId
    if (!modalId) return

    const modal = document.getElementById(modalId)
    if (!modal) return

    const modalController = this.application.getControllerForElementAndIdentifier(modal, "modal")
    if (modalController) {
      modalController.open()
    }
  }

  handleSubmitEnd(event) {
    if (event.detail.success) {
      this.close()
    }
  }

  copyStaffIds() {
    const mainForm = document.getElementById('teams-onboarding-form')
    if (!mainForm) return

    const staffIds = []
    const peopleInputs = mainForm.querySelectorAll('input[name^="people["]')
    peopleInputs.forEach(input => {
      const match = input.name.match(/people\[(\d+)\]/)
      if (match && match[1]) {
        const id = parseInt(match[1], 10)
        if (!staffIds.includes(id)) {
          staffIds.push(id)
        }
      }
    })

    const hiddenField = document.getElementById('csv_current_staff_ids')
    if (hiddenField) {
      hiddenField.value = staffIds.join(',')
    }
  }

  // CSV Upload functionality
  browseFile() {
    if (this.hasFileInputTarget) {
      this.fileInputTarget.click()
    }
  }

  selectFile(event) {
    const file = event.target.files[0]
    this.validateAndShowFile(file)
  }

  dragOver(event) {
    event.preventDefault()
    if (this.hasDropzoneTarget) {
      this.dropzoneTarget.classList.add("border-teal-400", "bg-teal-50")
    }
  }

  dragLeave() {
    if (this.hasDropzoneTarget) {
      this.dropzoneTarget.classList.remove("border-teal-400", "bg-teal-50")
    }
  }

  dropFile(event) {
    event.preventDefault()
    if (this.hasDropzoneTarget) {
      this.dropzoneTarget.classList.remove("border-teal-400", "bg-teal-50")
    }

    const file = event.dataTransfer.files[0]
    if (this.isValidCsv(file) && this.hasFileInputTarget) {
      this.fileInputTarget.files = event.dataTransfer.files
      this.validateAndShowFile(file)
    }
  }

  validateAndShowFile(file) {
    if (!this.hasFileNameTarget || !this.hasSubmitButtonTarget) return

    if (file && this.isValidCsv(file)) {
      this.fileNameTarget.textContent = file.name
      this.fileNameTarget.className = "text-xs text-teal-600 font-medium mt-2"
      this.submitButtonTarget.disabled = false
    } else {
      this.fileNameTarget.textContent = this.invalidCsvMessageValue
      this.fileNameTarget.className = "text-xs text-red-500 mt-2"
      this.submitButtonTarget.disabled = true
    }
  }

  isValidCsv(file) {
    if (!file) return false
    const validTypes = ["text/csv", "application/vnd.ms-excel"]
    return validTypes.includes(file.type) || file.name.endsWith(".csv")
  }

  closeDropdownOutside(event) {
    if (!this.element.contains(event.target)) {
      this.element.removeAttribute("open")
    }
  }

  // Shift modal functionality
  openShiftInfo(event) {
    event.preventDefault()
    const el = event.currentTarget

    const name = el.dataset.shiftName || "Shift"
    const start = el.dataset.shiftStart || "-"
    const end = el.dataset.shiftEnd || "-"
    const duration = el.dataset.shiftDuration || "-"
    const required = el.dataset.shiftRequired || "-"
    const weekdays = el.dataset.shiftWeekdays || "All days"
    const roles = el.dataset.shiftRoles || "Any role"
    const shiftDate = el.dataset.shiftDate || null

    if (this.hasTitleTarget) {
      this.titleTarget.textContent = name
    }
    if (this.hasTimeTarget) {
      this.timeTarget.textContent = `${start} - ${end}`
    }
    if (this.hasDurationTarget) {
      this.durationTarget.textContent = `${duration} hours`
    }
    if (this.hasRequiredTarget) {
      this.requiredTarget.textContent = required
    }
    if (this.hasWeekdaysTarget) {
      this.weekdaysTarget.textContent = shiftDate || weekdays || "All days"
    }
    if (this.hasRolesTarget) {
      this.rolesTarget.textContent = roles || "Any role"
    }

    this.openModalTarget()
  }

  // Open modal from calendar shift card click
  openFromCalendar(event) {
    event.preventDefault()
    const shiftCard = event.currentTarget

    const shiftName = shiftCard.dataset.shiftName || shiftCard.querySelector("strong")?.textContent || "Shift"
    const shiftTime = shiftCard.dataset.shiftTime || "-"
    const coverage = shiftCard.dataset.shiftCoverage || "0"
    const assigned = shiftCard.dataset.shiftAssigned || "0"
    const required = shiftCard.dataset.shiftRequired || "0"

    if (this.hasTitleTarget) {
      this.titleTarget.textContent = shiftName
    }
    if (this.hasTimeTarget) {
      this.timeTarget.textContent = shiftTime
    }
    if (this.hasRequiredTarget) {
      this.requiredTarget.textContent = `${assigned}/${required} assigned`
    }
    if (this.hasDurationTarget) {
      this.durationTarget.textContent = `${coverage}% coverage`
    }

    this.openModalTarget()
  }

  // Open/close the modal target (for shift modals where controller is on parent)
  openModalTarget() {
    if (this.hasModalTarget) {
      this.modalTarget.classList.remove("hidden")
      this.modalTarget.classList.add("flex")
      document.body.style.overflow = "hidden"
    }
  }

  closeModalTarget() {
    if (this.hasModalTarget) {
      this.modalTarget.classList.add("hidden")
      this.modalTarget.classList.remove("flex")
      document.body.style.overflow = ""
    }
  }

  closeOnModalBackdrop(event) {
    if (this.hasModalTarget && event.target === this.modalTarget) {
      this.closeModalTarget()
    }
  }

  // Day Detail Modal Methods
  openDayDetail(event) {
    event.preventDefault()
    const button = event.currentTarget
    const dateStr = button.dataset.date
    const formattedDate = button.dataset.formattedDate

    if (this.hasDayModalTitleTarget) {
      this.dayModalTitleTarget.innerHTML = `<i class="fas fa-calendar-day mr-2"></i>${formattedDate}`
    }

    // Get the shifts content from the hidden container
    const shiftsContainer = document.getElementById(`day-shifts-${dateStr}`)
    if (shiftsContainer && this.hasDayModalContentTarget) {
      // Copy the content and update frame IDs to use day-modal prefix
      // This allows Turbo Stream to update the cards in the day modal
      let content = shiftsContainer.innerHTML
      // Replace turbo-frame IDs from "shift-card-expanded-X" to "day-modal-shift-X"
      content = content.replace(/id="shift-card-expanded-/g, 'id="day-modal-shift-')
      this.dayModalContentTarget.innerHTML = content
    }

    if (this.hasDayModalTarget) {
      this.dayModalTarget.classList.remove("hidden")
      this.dayModalTarget.classList.add("flex")
      document.body.style.overflow = "hidden"
    }
  }

  closeDayModal() {
    if (this.hasDayModalTarget) {
      this.dayModalTarget.classList.add("hidden")
      this.dayModalTarget.classList.remove("flex")
      document.body.style.overflow = ""
    }
  }

  closeOnDayModalBackdrop(event) {
    if (event.target === this.dayModalTarget) {
      this.closeDayModal()
    }
  }

  // Shift Detail Modal - loads content via Turbo Frame from server
  openShiftDetail(event) {
    event.preventDefault()
    event.stopPropagation()

    const el = event.currentTarget
    const shiftId = el.dataset.shiftId
    const shiftName = el.dataset.shiftName || "Shift"
    const formattedDate = el.dataset.shiftDate || ""
    const dateIso = el.dataset.shiftDateIso || ""

    // Set title and date immediately for instant feedback
    if (this.hasShiftDetailTitleTarget) {
      this.shiftDetailTitleTarget.textContent = shiftName
    }
    if (this.hasShiftDetailDateTarget) {
      this.shiftDetailDateTarget.textContent = formattedDate
    }

    // Load content via Turbo Frame
    const frame = document.getElementById("shift-detail-modal-content")
    if (frame) {
      frame.src = `/admin/planning_generations/shift_detail?shift_id=${shiftId}&date=${dateIso || formattedDate}`
    }

    // Show modal
    if (this.hasShiftDetailModalTarget) {
      this.shiftDetailModalTarget.classList.remove("hidden")
      this.shiftDetailModalTarget.classList.add("flex")
      document.body.style.overflow = "hidden"
    }
  }

  closeShiftDetailModal() {
    if (this.hasShiftDetailModalTarget) {
      this.shiftDetailModalTarget.classList.add("hidden")
      this.shiftDetailModalTarget.classList.remove("flex")
      document.body.style.overflow = ""
    }
  }

  closeOnShiftDetailBackdrop(event) {
    if (event.target === this.shiftDetailModalTarget) {
      this.closeShiftDetailModal()
    }
  }

  // Load a URL into the modal's turbo-frame without triggering full page navigation
  loadFrame(event) {
    event.preventDefault()
    const url = event.currentTarget.dataset.url
    if (!url) return

    const frame = this.element.querySelector("turbo-frame")
    if (frame) {
      frame.src = url
    }
  }

}
